import { observable, action, computed } from 'mobx'
import type { NavigateFn } from '@/RootStore'
import { Id } from '@/infra/id'
import { OCRScopeWork } from '@/features/jobs/scoping/OCRScopeWork'
import { type OCRScopeWorkStore } from '@/features/jobs/scoping/OCRScopeWorkStore'
import {
  EMPTY,
  filter,
  mergeMap,
  type Observable,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs'
import type { GetCurrentMemberFn } from '@/features/iam'
import { task } from 'mobx-task'
import { toStream } from '@/utils/mobx-util'
import { type OCRScopingJobsPermissionViewModel } from '@/features/jobs/scoping/OCRScopingJobsPermissionViewModel'
import { OCRConfirmReviewDialogViewModel } from '@/features/jobs/scoping/components/OCRConfirmReviewDialog/OCRConfirmReviewDialogViewModel'
import { OCRConfirmSubmitDialogViewModel } from '@/features/jobs/scoping/components/OCRConfirmSubmitDialog/OCRConfirmSubmitDialogViewModel'
import { OCRConfirmArchiveDialogViewModel } from '@/features/jobs/scoping/components/OCRConfirmArchiveDialog/OCRConfirmArchiveDialogViewModel'
import { links } from '@/utils/link'
import { PrefilledDraftInput } from '@/features/jobs/drafting/PrefilledDraftInput'
import { type CreateJobDialogViewModel } from '@/features/jobs/drafting/components/CreateJobDialog/CreateJobDialogViewModel'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { type JobCreationRootViewModel } from '@/features/jobs/drafting/JobCreationRootViewModel'
import { type LegendTemplate } from '@/features/jobs/legends/PublishedLegend'
import type {
  DownloadDocumentFn,
  SearchDocumentsFn,
} from '@/features/documents'
import type { Document } from '@/features/documents/Document'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * View model for managing the OCR scope work details.
 */
export class OCRScopingJobMainPanelViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Emits when a job is created from the link manually dialog.
   */
  private readonly _linkManuallyJobCreated$ = new Subject<
    Observable<{ id: Id }>
  >()

  readonly confirmReviewDialogVm

  readonly confirmSubmitDialogVm

  readonly confirmArchiveDialogVm

  @observable accessor selectedScopeWork = OCRScopeWork.none

  @observable accessor legendTemplate: LegendTemplate | null = null

  @observable accessor document: Document | null = null

  constructor(
    private readonly _ocrScopingJobsPermissionVm: OCRScopingJobsPermissionViewModel,
    private readonly _ocrScopeWorkStore: OCRScopeWorkStore,
    private readonly _searchDocuments: SearchDocumentsFn,
    private readonly _downloadDocument: DownloadDocumentFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _navigate: NavigateFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _jobCreationRootVm: JobCreationRootViewModel,
    readonly createJobDialogVm: CreateJobDialogViewModel,
  ) {
    this.confirmReviewDialogVm = new OCRConfirmReviewDialogViewModel(
      this._ocrScopeWorkStore,
      this._newAlert,
      this._createAlertErrorHandler,
    )
    this.confirmSubmitDialogVm = new OCRConfirmSubmitDialogViewModel(
      this._jobCreationRootVm,
      this._navigate,
      this._newAlert,
    )
    this.confirmArchiveDialogVm = new OCRConfirmArchiveDialogViewModel(
      this._ocrScopeWorkStore,
      this._newAlert,
      this._createAlertErrorHandler,
    )

    this._lifecycle
      .manage(
        this._ocrScopeWorkStore.events$.pipe(
          tap((event) => {
            const scopeWorkId =
              event.type === 'Archived'
                ? event.scopeWorkId
                : event.type === 'Submitted'
                  ? event.scopeWork.id
                  : Id.none
            if (
              Id.some(scopeWorkId) &&
              this.selectedScopeWork.id === scopeWorkId
            ) {
              this._navigate(links.scopingJobs())
            }
          }),
        ),
      )
      .manage(
        // A scope work without a job gets one once scoping completes.
        toStream(() => this.selectedScopeWork, true).pipe(
          switchMap((scopeWork) =>
            scopeWork.jobId
              ? EMPTY
              : toStream(() => scopeWork.jobId).pipe(filter(Boolean), take(1)),
          ),
          // The detail body renders a `selectJob` failure.
          switchMap(() =>
            this.selectJob(
              this.selectedScopeWork,
              this._lifecycle.signal,
            ).catch(() => undefined),
          ),
        ),
      )
      .manage(
        this._linkManuallyJobCreated$.pipe(
          switchMap((jobCreated$) => jobCreated$),
          // The error alert already reported a failure.
          mergeMap((job) => this.linkManually(job.id).catch(() => undefined)),
        ),
      )
  }

  @computed
  get editor() {
    return this._jobCreationRootVm.current
  }

  @computed
  get hasJob() {
    return Id.some(this.selectedScopeWork.jobId)
  }

  @computed
  get canSubmitJob() {
    return (
      this._jobCreationRootVm.canSubmit && this.editor.intakeQuestions.completed
    )
  }

  @computed
  get canManageOCRScopingJobs() {
    return this._ocrScopingJobsPermissionVm.canManageOCRScopingJobs
  }

  @computed
  get hasScopeWork() {
    return this.selectedScopeWork !== OCRScopeWork.none
  }

  @computed
  get isScopingInProgress() {
    return this.selectedScopeWork.status === 'ScopingInProgress'
  }

  @computed
  get isErrorOccurred() {
    return this.selectedScopeWork.status === 'ErrorOccurred'
  }

  @computed
  get isUnderReview() {
    return (
      this.selectedScopeWork.status === 'ReviewRequired' ||
      this.selectedScopeWork.status === 'ReviewSuggested'
    )
  }

  @computed
  get isReadyToSubmit() {
    return this.selectedScopeWork.status === 'ReadyToSubmit'
  }

  @computed
  get isCompleted() {
    return this.selectedScopeWork.status === 'Completed'
  }

  /**
   * Whether the status is not one that needs attention.
   */
  @computed
  get isStatusHealthy() {
    return this.isScopingInProgress || this.isReadyToSubmit || this.isCompleted
  }

  @computed
  get disabled() {
    return this.isPending || this.isCompleted || !this.editor.job.isDraft
  }

  @computed
  get isPending() {
    return (
      this.fetch.pending ||
      this.confirmArchiveDialogVm.archive.pending ||
      this.rerun.pending ||
      this.linkManually.pending ||
      this.confirmReviewDialogVm.review.pending ||
      this.confirmSubmitDialogVm.submit.pending
    )
  }

  /**
   * Whether the scoping job is the one showing.
   */
  isSelected(scopeWork: OCRScopeWork) {
    return this.selectedScopeWork.id === scopeWork.id
  }

  @action.bound
  setLegendTemplate(legendTemplate?: LegendTemplate) {
    this.legendTemplate = legendTemplate ?? null
  }

  @action.bound
  async openLinkManuallyDialog() {
    // The dialog renders a failed `edit`.
    const editor = await this.createJobDialogVm
      .open(
        undefined,
        new PrefilledDraftInput(
          this.selectedScopeWork.legendId,
          this.selectedScopeWork.userPublicId,
          Id.some(this.selectedScopeWork.teamId)
            ? this.selectedScopeWork.teamId
            : null,
          [],
          undefined,
        ),
      )
      .catch(() => null)
    if (!editor) {
      return
    }

    if (editor.jobWasCreated) {
      await this.linkManually(editor.job.id)
      return
    }
    this._linkManuallyJobCreated$.next(editor.jobCreated$)
  }

  /**
   * Asks the user to confirm, then archives the scoping job.
   */
  @action.bound
  async confirmArchive() {
    if (!this.hasScopeWork) {
      return
    }

    await this.confirmArchiveDialogVm.prompt({
      scopeWorkId: this.selectedScopeWork.id,
      signal: this._lifecycle.signal,
    })
  }

  /**
   * Asks the user to confirm, then moves the job to ready to submit.
   */
  @action.bound
  async confirmReview() {
    if (!this.canManageOCRScopingJobs || !this.isUnderReview) {
      return
    }

    await this.confirmReviewDialogVm.prompt({
      scopeWorkId: this.selectedScopeWork.id,
      signal: this._lifecycle.signal,
    })
  }

  /**
   * Asks the user to confirm the submission of the job.
   */
  @action.bound
  async confirmSubmit() {
    if (!this.isReadyToSubmit || !this.canSubmitJob) {
      return
    }

    await this.confirmSubmitDialogVm.prompt(this._lifecycle.signal)
  }

  @action.bound
  downloadDocument() {
    if (!this.document) {
      return
    }

    Promise.resolve(this._downloadDocument(this.document, false))
      .catch(this._createAlertErrorHandler('Downloading the document failed'))
      .catch(() => undefined)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Loads the scoping job of the route; run by the route loader.
   *
   * @returns The selected scoping job, or `null` when it does not exist or is
   * completed.
   */
  fetch = task(async (scopeWorkId: Id, signal?: AbortSignal) => {
    if (scopeWorkId === Id.none) {
      this._setSelected(OCRScopeWork.none, null, null)
      return OCRScopeWork.none
    }

    const scopeWork = await this._ocrScopeWorkStore.fetchScopeWork({
      workspaceId: this.canManageOCRScopingJobs
        ? Id.none
        : Id.assert(this._getCurrentMember().workspace.id),
      scopeWorkId,
      signal,
    })

    if (!scopeWork || scopeWork.status === 'Completed') {
      return null
    }

    await this.selectJob(scopeWork, signal)
    return scopeWork
  })

  selectJob = task(async (scopeWork: OCRScopeWork, signal?: AbortSignal) => {
    if (!scopeWork.jobId) {
      this._setSelected(scopeWork, null, null)
      return
    }

    await this._jobCreationRootVm.edit(
      scopeWork.jobId,
      PrefilledDraftInput.empty(),
      () => this.disabled,
    )

    const templates = await this._ocrScopeWorkStore.fetchLegendTemplates(
      {
        id: Id.assert(scopeWork.legendId),
        version: scopeWork.legendVersion,
      },
      signal,
    )
    const template = templates.find((i) => i.id === scopeWork.templateId) ?? {
      id: Id.assert(scopeWork.templateId),
      name: Id.assert(scopeWork.templateId),
    }

    const documents = await this._searchDocuments(
      { context: 'Job', jobId: scopeWork.jobId },
      signal,
    )
    const document =
      documents.data?.find((d) => d.title === 'Previous Tax Return') ?? null

    this._setSelected(scopeWork, template, document)
  })

  rerun = task.resolved(async () => {
    if (!this.canManageOCRScopingJobs || !this.isErrorOccurred) {
      return
    }

    await this._ocrScopeWorkStore
      .rerun(this.selectedScopeWork.id)
      .catch(this._createAlertErrorHandler('Rerunning the scoping job failed'))
    this._newAlert()
      .saying('Success!', 'Scoping job was rerun successfully.')
      .withState('success')
      .autoDismiss()
      .show()
  })

  linkManually = task.resolved(async (jobId: Id) => {
    if (!this.canManageOCRScopingJobs || !this.isErrorOccurred) {
      return
    }

    await this._ocrScopeWorkStore
      .linkManually({
        scopeWorkId: this.selectedScopeWork.id,
        jobId,
      })
      .catch(this._createAlertErrorHandler('Linking the job failed'))
    this._newAlert()
      .saying('Success!', 'Job was linked successfully.')
      .withState('success')
      .autoDismiss()
      .show()
  })

  @action.bound
  private _setSelected(
    scopeWork: OCRScopeWork,
    legendTemplate: LegendTemplate | null,
    document: Document | null,
  ) {
    this.selectedScopeWork = scopeWork
    this.legendTemplate = legendTemplate
    this.document = document
  }
}
